import { types, getParent, getRoot, flow } from 'mobx-state-tree';
import MarketPlace from '../../models/MarketPlace';
import MarketPlaceType from '../../models/SettingsStore/MarketPlaceType';
import Account from '../../models/Account';

const convertSourceMarketsEnumsToDestination = eNum => {
  switch (eNum) {
    case 'AZ_IT':
      return 'EBAY_IT';
    case 'AZ_DE':
      return 'EBAY_DE';
    case 'AZ_FR':
      return 'EBAY_FR';
    case 'AZ_ES':
      return 'EBAY_ES';
    default:
      return '';
  }
};

const Markets = types
  .model('Markets', {
    destinationTypes: types.optional(types.array(MarketPlaceType), []),
    destinations: types.optional(types.array(MarketPlace), []),
    defaultDestination: types.optional(MarketPlace, {}),
    selectedDestination: types.maybe(MarketPlace),

    sourceTypes: types.optional(types.array(MarketPlaceType), []),
    allSources: types.optional(types.array(MarketPlace), []),
    defaultSource: types.maybe(MarketPlace),
    selectedSource: types.maybe(MarketPlace),

    sellersAccounts: types.optional(types.array(Account), []),
    defaultSellersAccount: types.optional(Account, {}),
    selectedAccount: types.maybe(Account),
    hasSellerAccounts: types.frozen,
  })
  .views(self => ({
    get transport5007() {
      return getParent(self).transport5007;
    },
    get defaultCurrencyId() {
      return getParent(self).userProfile.defaultCurrencyId;
    },
    get defaultSourceMarketplace() {
      return getParent(self).userProfile.defaultSourceMarketplace;
    },
    get accountCode() {
      return getRoot(self).uiStore.sessionStore.accountCode;
    },
    get sources() {
      return self.allSources;
    },
    get isBetaUser() {
      return getParent(self).userProfile.isBetaUser;
    },
    get getDestinations() {
      return self.destinations;
    },
    get getDefaultDestination() {
      return self.defaultDestination;
    },
  }))
  .actions(self => {
    const fetchSources = flow(function* fetchSources() {
      try {
        const { data } = yield self.transport5007.fetch(
          `account/v1/source-marketplaces?currency=${self.defaultCurrencyId}&sourceMarketplace=${self.defaultSourceMarketplace}`,
        );
        self.allSources = self.isBetaUser ? data : data.filter(market => !market.id.includes('CJ'));
        self.defaultSource = data[0]; // eslint-disable-line prefer-destructuring
      } catch (err) {
        console.log('getting sources failed: ', err);
      }
    });

    const fetchDestinations = flow(function* fetchDestinations() {
      try {
        const params = {
          currency: self.defaultCurrencyId,
          destinationMarketplace : convertSourceMarketsEnumsToDestination(self.defaultSourceMarketplace)
        };
        const { data } = yield self.transport5007.fetch(
          'seller-account/v1/destination-marketplaces',
          params,
        );
        self.destinations = data;
        self.defaultDestination = data[0]; // eslint-disable-line prefer-destructuring
      } catch (err) {
        console.log('getting destinations failed: ', err);
      }
    });

    const fetchSellersAccounts = flow(function* fetchSellersAccounts(
      destMarketPlaceId = self.defaultDestination.id,
    ) {
      try {
        const { data } = yield self.transport5007.fetch(
          `seller-account/v1/seller-accounts?&marketplaceId=${destMarketPlaceId}`,
          {},
          {},
        );
        self.sellersAccounts = [...data];
        self.defaultSellersAccount = data[0]; // eslint-disable-line prefer-destructuring
        self.hasSellerAccounts = data.length > 0 ?? false;
      } catch (err) {
        console.log('fetching sellers accounts failed: ', err);
      }
    });

    const fetchDestinationTypes = flow(function* fetchDestinationTypes() {
      try {
        const { data } = yield self.transport5007.fetch(
          `seller-account/v1/destination-marketplace-types?currencyId=${self.defaultCurrencyId}`,
        );
        self.destinationTypes = data;
      } catch (err) {
        console.log('getting destinationTypes failed: ', err);
      }
    });

    const fetchDestinationsOfType = flow(function* fetchDestinationsOfType(type) {
      try {
        const params = {
          currency: self.defaultCurrencyId,
          destinationMarketplace : convertSourceMarketsEnumsToDestination(self.defaultSourceMarketplace),
          fields: 'authorizationUrl',
          type,
        };
        const { data } = yield self.transport5007.fetch(
          'seller-account/v1/destination-marketplaces',
          params,
        );
        return data;
      } catch (err) {
        console.log('getting destinationTypes failed: ', err);
        return null;
      }
    });

    const initMarkets = flow(function* initMarkets() {
      yield Promise.allSettled([
        self.fetchDestinationTypes(),
        self.fetchDestinations(),
        self.fetchSources(),
      ]);
      yield self.fetchSellersAccounts();
    });
    return {
      initMarkets,
      fetchSources,
      fetchDestinations,
      fetchSellersAccounts,
      fetchDestinationTypes,
      fetchDestinationsOfType,
    };
  });

export default Markets;
